import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, MessageCircle, ShieldCheck, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { whatsappLink } from "@/lib/site";
import Reveal from "@/components/Reveal";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, subtotal, clearCart } = useShop();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", pincode: "" });
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openRazorpay = async (data) => {
    const ok = await loadRazorpay();
    if (!ok) {
      toast.error("Could not load the payment gateway. Please retry.");
      setBusy(false);
      return;
    }
    const rzp = new window.Razorpay({
      key: data.razorpay_key_id,
      amount: data.amount_paise,
      currency: "INR",
      name: "Sashti Jewels",
      description: `Order ${data.order_id}`,
      order_id: data.razorpay_order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#9A792F" },
      handler: async (res) => {
        try {
          await api.post("/orders/verify", { order_id: data.order_id, ...res });
          navigate(`/payment/success?order_id=${data.order_id}`);
        } catch (err) {
          toast.error("Payment verification failed — we'll confirm on WhatsApp.");
          setBusy(false);
        }
      },
      modal: {
        ondismiss: () => {
          setBusy(false);
          navigate("/payment/cancel");
        },
      },
    });
    rzp.open();
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.qty })),
        customer: form,
        origin_url: window.location.origin,
      });
      if (data.mode === "razorpay") {
        await openRazorpay(data);
      } else {
        setDemo(data);
        setBusy(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const demoConfirm = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/demo-confirm/${demo.order_id}`);
      navigate(`/payment/success?order_id=${demo.order_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (cart.length === 0 && !demo) {
    return (
      <main className="pt-32 pb-24 px-6 text-center" data-testid="checkout-empty">
        <ShoppingBag size={40} className="mx-auto text-gold2" />
        <h1 className="mt-5 font-display text-4xl font-semibold">Your cart is empty</h1>
        <p className="mt-3 text-sm text-ink2">Add an heirloom before stepping to the checkout.</p>
        <Link to="/shop" className="btn-gold mt-8">Back to the Atelier</Link>
      </main>
    );
  }

  const valid = form.name && form.email && form.phone && form.address;

  return (
    <main className="pt-24 lg:pt-32">
      <div className="mx-auto max-w-[1100px] px-4 sm:px-8 lg:px-16 py-10 lg:py-14" data-testid="checkout-page">
        <Reveal y={16}>
          <p className="overline-tag">Secure Checkout</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold">Complete your order</h1>
        </Reveal>

        {demo ? (
          <div className="mt-10 card-surface p-6 sm:p-10" data-testid="checkout-modal">
            <p className="overline-tag">Test Mode</p>
            <h2 className="mt-2 font-display text-3xl font-semibold">Confirm your payment</h2>
            <div className="gold-hr my-5" />
            <p className="text-sm text-ink2 leading-relaxed">
              The Razorpay keys aren&apos;t connected yet, so this is a simulated payment step.
              Once you share your Razorpay Key ID &amp; Secret, this screen is replaced by the
              real Razorpay checkout (UPI, cards, netbanking).
            </p>
            <div className="mt-6 flex justify-between items-baseline border border-line p-4">
              <span className="font-cinzel text-[11px] uppercase text-ink2" style={{ letterSpacing: "0.2em" }}>Amount</span>
              <span className="font-mono text-2xl text-ink">{inr(subtotal)}</span>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={demoConfirm} disabled={busy} className="btn-gold flex-1 justify-center" data-testid="demo-pay-confirm">
                {busy ? "Processing…" : "Simulate Successful Payment"}
              </button>
              <button onClick={() => navigate("/payment/cancel")} className="btn-ghost-gold justify-center" data-testid="demo-pay-cancel">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal delay={0.08}>
              <div className="card-surface p-6 sm:p-8">
                <p className="overline-tag">Delivery Details</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <input required placeholder="Full name" className="field" value={form.name} onChange={set("name")} data-testid="checkout-name" />
                  <input required type="email" placeholder="Email" className="field" value={form.email} onChange={set("email")} data-testid="checkout-email" />
                  <input required placeholder="Phone / WhatsApp" className="field" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" />
                  <input placeholder="City" className="field" value={form.city} onChange={set("city")} data-testid="checkout-city" />
                  <textarea required rows={2} placeholder="Delivery address" className="field sm:col-span-2 resize-none" value={form.address} onChange={set("address")} data-testid="checkout-address" />
                  <input placeholder="PIN code" className="field" value={form.pincode} onChange={set("pincode")} data-testid="checkout-pincode" />
                </div>
                <div className="gold-hr my-6" />
                <div className="flex items-center gap-2.5 text-xs text-ink2">
                  <ShieldCheck size={15} className="text-gold" />
                  Payments are processed by Razorpay — card details never touch our servers.
                </div>
                <p className="mt-3 text-center text-[11px] leading-relaxed text-ink3" data-testid="checkout-policy-consent">
                  By placing this order you agree to our{" "}
                  <Link to="/policies/terms" className="text-gold underline underline-offset-2 hover:text-gold2">Terms of Service</Link>,{" "}
                  <Link to="/policies/privacy" className="text-gold underline underline-offset-2 hover:text-gold2">Privacy Policy</Link> and{" "}
                  <Link to="/policies/refund-return" className="text-gold underline underline-offset-2 hover:text-gold2">Refund &amp; Return Policy</Link>.
                </p>
                <button
                  onClick={pay}
                  disabled={!valid || busy}
                  className="btn-gold mt-6 w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="checkout-pay-button"
                >
                  <Lock size={13} /> {busy ? "Opening payment…" : `Pay ${inr(subtotal)} Securely`}
                </button>
                {!valid && <p className="mt-3 text-center text-[11px] text-ink3">Fill name, email, phone and address to continue.</p>}
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="card-surface p-6 sm:p-8">
                <p className="overline-tag">Your Pieces · {cart.length}</p>
                <div className="mt-5 space-y-4 max-h-[340px] overflow-y-auto pr-1">
                  {cart.map((i) => (
                    <div key={i.id} className="flex gap-3.5 items-center" data-testid={`checkout-item-${i.id}`}>
                      <div className="h-16 w-14 shrink-0 overflow-hidden bg-bg2 border border-line">
                        {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-base font-semibold truncate">{i.name}</p>
                        <p className="font-mono text-[10px] text-ink3 uppercase">Qty {i.qty}</p>
                      </div>
                      <span className="font-mono text-xs text-ink">{inr(i.price * i.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="gold-hr my-5" />
                <div className="flex justify-between items-baseline">
                  <span className="font-cinzel text-[11px] uppercase text-ink2" style={{ letterSpacing: "0.2em" }}>Total</span>
                  <span className="font-mono text-2xl text-ink" data-testid="checkout-total">{inr(subtotal)}</span>
                </div>
                <p className="mt-2 font-mono text-[10px] text-ink3">Insured shipping on us · Care card included with every order</p>
                <a
                  href={whatsappLink("Hello Sashti Jewels, I have a question about my order.")}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center gap-2 text-xs font-cinzel uppercase text-gold border border-gold/40 py-3 hover:bg-gold hover:text-[#241A08] transition-colors"
                  style={{ letterSpacing: "0.2em" }}
                  data-testid="checkout-whatsapp-help"
                >
                  <MessageCircle size={13} /> Prefer help? WhatsApp us
                </a>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </main>
  );
}
